/* eslint-disable require-jsdoc */
/**
 * Meetings page.
 *
 * @module app/pages/MeetingsPage
 */
import _ from 'lodash';
import moment from 'moment-timezone';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import DeviceDetector from '../components/DeviceDetector';
import EventViewAdapter from '../views/EventViewAdapter';
import FirstTimeUserJourney from '../components/FirstTimeUserJourney';
import LazyLoadingImage from '../components/LazyLoadingImage';
import MessageBusTopics from '../../common/MessageBusTopics';
import RacecardResultsViewProvider from '../views/RacecardResultsViewProvider';
import ResultsApiAdapter from '../components/ResultsApiAdapter';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import StickyHeader from '../components/StickyHeader';
import Streaming from '../components/Streaming';
import SwiperNavigation from '../components/SwiperNavigation';
import Tabs from '../components/Tabs';
import Translator from '../components/Translator';
import URLProvider from '../components/URLProvider';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';

/**
 * OffCanvas Wrapper jquery element object
 *
 * @type {Object}
 *
 * @private
 */
var $offCanvas = null;

/**
 * Mobile Contextual Nav Wrapper jquery element object
 *
 * @type {Object}
 *
 * @private
 */
var $contextualNav = null;

/**
 * A class for canvas wrapper to make left menu active
 *
 * @type {Object}
 *
 * @private
 */
var activeMenuClass = '';

/**
 * Internal array with the IDs of the message bus subcriptions
 *
 * @type {Array}
 */
var messageBusSubscriptions = [];

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Internal flag to tack the races loaded in the page to compose a
 * multiple race selection
 *
 * @type {Object}
 */
var couponRaces = {};

/**
 * Maximum number of coupon races
 *
 * @type {number}
 */
var maxCouponRaces = 8;

/**
 * Holds references to created players
 *
 * @type {Array<Object>}
 */
let players = [];

/**
 * Defines how long to wait since we receive a notification of bet placed
 * Till we do a refresh on the streamings service to allow some propagation
 *
 * @type {Integer}
 */
var streamingsReInitDelay = 2000;

var prefs;

/**
 * Main page title
 * Title is value might be dynamicaly update due navigation between meetings/next off views
 *
 * @type {Object}
 */
var $meetingsMainTitle = BasePage.jQuery('#meetingsMainTitle');

/**
 * Cached jQuery ref for the next off group's clickable header
 *
 * @type {Object}
 *
 * @private
 */
var $nextOffHeader;

/**
 * Object containing sort configurations for the sortRaceCardAction
 *
 * @type {Object}
 */
var sortObjects = {
    'runnerNumber': {
        valueItem: 'order',
        htmlValue: false,
        sortFunction: sortItemsByValue,
    },
    'drawNumber': {
        valueItem: 'drawNumber',
        htmlValue: true,
        sortFunction: sortItemsByValue,
    },
    'selectionName': {
        valueItem: 'name',
        htmlValue: false,
        sortFunction: sortItemsByAlpha,
    },
    'rpRank': {
        valueItem: 'rpRank',
        htmlValue: true,
        sortFunction: sortItemsByValue,
    },
    'price': {
        valueItem: ['currentPriceNum', 'currentPriceDen'],
        htmlValue: false,
        sortFunction: sortItemsByPrice,
    },
};

/**
 * Returns whether the number is between 3 and 21
 *
 * @param {Number} the position number
 *
 * @returns {Boolean}
 *
 * @private
 */
var isNumberBetween3And21 = function (n) {

    return n > 3 && n < 21;
};

/**
 * Binds the click handler for the next-off header
 *
 * @return {void}
 *
 * @private
 */
var bindNextOffToggle = function bindNextOffToggle() {

    $nextOffHeader = BasePage.jQuery('#next-off-header');

    $nextOffHeader.off('click.nextOffToggle').on('click.nextOffToggle', toggleNextOff);
};

/**
 * Toggles the -opened class on the next off group
 *
 * @return {void}
 *
 * @private
 */
var toggleNextOff = function toggleNextOff() {

    $nextOffHeader.parent().toggleClass('-opened');
};

/**
 * Returns 'st' or something else
 *
 * @param {Number} the position number
 *
 * @returns {String}
 *
 * @private
 */
var ifNumberEqualsOneReturnSt = function (n) {

    return n === 1 ? 'st' : ifNumberEqualsTwoReturnNd(n);
};

/**
 * Returns 'nd' or something else
 *
 * @param {Number} the position number
 *
 * @returns {String}
 *
 * @private
 */
var ifNumberEqualsTwoReturnNd = function (n) {

    return n === 2 ? 'nd' : ifNumberEqualsThreeReturnRd(n);
};

/**
 * Returns 'rd' or something else
 *
 * @param {Number} the position number
 *
 * @returns {String}
 *
 * @private
 */
var ifNumberEqualsThreeReturnRd = function (n) {

    return n === 3 ? 'rd' : 'th';
};

/**
 * Returns a contraction (1 -> st)
 *
 * @param {Number} the position number
 *
 * @returns {String}
 *
 * @private
 */
var getPositionContraction = function (n) {

    return isNumberBetween3And21(n % 100) ?
        'th' : ifNumberEqualsOneReturnSt(n % 10);
};

/**
 * Last clicked manager literal to retain button highlight through fragment reload
 *
 * @type {Object}
 *
 */
var nav = {

    _id: null,

    _className: 'race-meeting__selection',

    _classNameSelected: 'race-meeting__selection--selected',

    /**
     * Attempt to store a current button selection into the cache
     *
     * @returns {Void}
     *
     * @private
     */
    init: function () {

        var elements = content.find('.' + this._classNameSelected);
        if (elements.length) {
            this._id = this.getElementUid(BasePage.jQuery(elements[0]));
        }
        resetRacecardMultiSelection();
    },

    /**
     * Store the button by by of an I.D. The button can't be stored directly due to partial reload
     *
     * @param {Object} jQuery object
     *
     * @returns {Void}
     *
     * @private
     */
    getElementUid: function (jElement) {

        return jElement.attr('data-uid');
    },

    /**
     * Set the cache and button className by of an I.D.
     * The button can't be stored directly due to partial reload
     *
     * @param {Object} jQuery object
     *
     * @returns {Void}
     *
     * @private
     */
    set: function (jElement) {

        // rem/apply css class
        var classNameSelected = this._classNameSelected;
        var className = '.' + this._className;

        var dontStore = function () {

            var hasLength = function (jElement) {

                if (!jElement.length) {
                    return true;
                }
            };

            if (jElement) {

                content.find(className).removeClass(classNameSelected);
                jElement.addClass(classNameSelected);

            } else {

                return hasLength(content.find(className));
            }
        };

        if (!dontStore()) {

            // store
            this._id = this.getElementUid(jElement);
        }
    },

    /**
     * Matches a button to a previous selection
     *
     * @param {Object} DOM Element
     *
     * @returns {Boolean} true is match
     *
     * @private
     */
    applyCache: function () {

        var self = this;

        // Remove selection from anything
        content.find('.' + this._classNameSelected)
            .removeClass(this._classNameSelected);

        var matcher = function () {

            // jQuery doesn't support some, so convert to an array then use proper JS
            content.find('.' + self._className).toArray().some(function (element) {
                var jElement = BasePage.jQuery(element);
                if (self.getElementUid(jElement) === self._id) {
                    jElement.addClass(self._classNameSelected);
                    return true;
                }
            });

        };

        // attempt to match the cache id to the current buttons
        if (self._id) {
            matcher();
        }
    },


};

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Formats distances into the required types
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardDataformatDistances = function (data) {

    var yards = data.distance_yards; //jshint ignore:line

    if (yards) {

        var furlongs = yards / 220;
        data.distance = {
            yards: yards,
            furlongs: Math.round(furlongs),
            miles: Math.round(furlongs * 8),
        };
    }
};

/**
 * Parses non-runners into it's own key
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardCalculateHasNonRunners = function (data) {

    /**
     * Detects if non runner
     *
     * @param {Object} Runner Data
     *
     * @returns {Boolean} true is match
     *
     * @private
     */

    var isNonRunner = function (runner) {

        return !runner.is_runner; //jshint ignore:line
    };

    data.nonRunners = data.runners.filter(isNonRunner);
};

/**
 * Finds and adds second favourite to the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFindFavRunner = function (data) {

    /**
     * Marks a fav1 runner
     *
     * @param {Object} Runner data
     *
     * @returns {Boolean} true is match
     *
     * @private
     */
    var isFirst = function (runner) {

        if (runner.is_1_fav) { //jshint ignore:line

            data.favourites.first = runner;
            return true;
        }
    };

    data.runners.some(isFirst);
};

/**
 * Finds and adds second favourite to the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFindFavRunner2 = function (data) {

    /**
     * Marks a fav1 runner
     *
     * @param {Object} Runner data
     *
     * @returns {Boolean} true is match
     *
     * @private
     */
    var isSecond = function (runner) {

        if (runner.is_2_fav) { //jshint ignore:line

            data.favourites.second = runner;
            return true;
        }
    };

    data.runners.some(isSecond);
};

/**
 * Filters runners to exclude non-runners.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFilterRunners = function (data) {

    /**
     * Detects is runner
     *
     * @param {Object} Runner data
     *
     * @returns {Boolean} true is match
     *
     * @private
     */
    var isRunner = function (runner) {

        return runner.is_runner; //jshint ignore:line
    };

    data.runners = data.runners.filter(isRunner);
};

/**
 * Adds has runner SP to the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardCalculateHasRunnerSP = function (data) {

    /**
     * Detects if has price
     *
     * @param {Object} Runner data
     *
     * @returns {Boolean} true is match
     *
     * @private
     */
    var hasSP = function (runner) {

        return runner.start_price || runner.live_price; //jshint ignore:line
    };

    data.hasSP = data.runners.some(hasSP);
};

/**
 * Adds has dividends flag to the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardCalculateHasDividends = function (data) {

    /**
     * Detects if has dividends
     *
     * @param {Object} Runner data
     *
     * @returns {Boolean} true is match
     *
     * @private
     */
    var hasDividend = function (runner) {

        return runner.dividends;
    };

    data.hasDividends = data.runners.some(hasDividend);
};

/**
 * Formats off date on the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFormatOffDate = function (data) {

    if (data.off_date) { //jshint ignore:line

        data.off_date = BasePage.Date.format(data.off_date); //jshint ignore:line
    }
};

/**
 * Formats winning time on the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFormatWinningTime = function (data) {

    if (data.winning_time) { //jshint ignore:line

        data.winning_time = BasePage.Date.format(data.winning_time); //jshint ignore:line
    }
};

/**
 * Flags the data has having dead heat runners.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardCalculateDeadHeat = function (data) {

    var buffer = [];

    data.hasDeadHeat = data.runners && !data.runners.every(function (runner) {
        if (buffer.indexOf(runner.position) !== -1) {
            return false;
        }
        buffer.push(runner.position);
        return true;
    });

};

/**
 * Adds extra param needed by the result template
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardAddDisplayData = function (data) {

    var isAussieDogs = BasePage.jQuery('.racecardResult').data('is-aussie-dogs');
    data.isAussieDogs = isAussieDogs;
};

/**
 * Formats start date on the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFormatStartDate = function (data) {

    if (data.start_date) { //jshint ignore:line

        data.start_date = BasePage.Date.format(data.start_date); //jshint ignore:line
    }
};

/**
 * Formats rule 4 date/times(s).
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardFormatRule4Dates = function (data) {
    var rules = data.rule4_deductions; //jshint ignore:line

    if (rules) {
        rules.forEach(function (rule) {

            rule.datetime_from = BasePage.Date.format(data.start_date, 'hh:mm:ss'); //jshint ignore:line
            rule.datetime_to = BasePage.Date.format(data.off_date, 'hh:mm:ss'); //jshint ignore:line
        });
    }
};

/**
 * Adds position contractions onto the object.
 *
 * @params {Object} result racecard data
 *
 * @returns {Void}
 *
 * @private
 */
var resultsRacecardCalculatePostionExtentions = function (data) {

    /**
     * A function for the loop
     *
     * @params {Object} A runner
     *
     * @returns {Void}
     *
     * @private
     */
    var createExt = function (runner) {

        runner.positionExt = getPositionContraction(runner.position);
    };

    data.runners.forEach(createExt);
};

/**
 * Unsubscribe to all the message bus notification added to the unsubscribe array
 *
 * @returns     {void}
 *
 * @private
 */
var unsubscribeMessageBus = function () {

    /**
     * Unsubscribe to a single message bus notification
     *
     * @returns     {void}
     *
     * @private
     */
    var unsubscribeMessageBusNotification = function (element) {

        if (window.WH && window.WH.messageBus && window.WH.messageBus.unsubscribe) {

            window.WH.messageBus.unsubscribe(element);
        }
    };

    messageBusSubscriptions.forEach(unsubscribeMessageBusNotification);
};

/**
 * Handles menu functions
 *
 * @returns {Object}
 *
 * @private
 */
var menuHandler = (function () {

    var selectedMenuId = -1;
    var loading = false;

    return {

        /**
         * Gets the loading status
         *
         * @returns {boolean}
         *
         * @private
         */
        getLoadingStatus: function () {

            return loading;
        },

        /**
         * Updates the loading status
         *
         * @param   {Boolean} status    loadding status
         *
         * @private
         */
        udpateLoadingStatus: function (status) {

            loading = status;
        },

        /**
         * Get the selected menu id
         *
         * @returns {Integer}
         *
         * @private
         */
        getSelectedMenuId: function () {

            return selectedMenuId;
        },

        /**
         * Updates the selected menu id
         *
         * @param   {Integer} id    menu id
         *
         * @returns {void}
         *
         * @private
         */
        updateSelectedMenuId: function (id) {

            selectedMenuId = id;
        },

        /**
         * Guesses the selected menu id from the DOM element
         *
         * @returns {Integer}
         *
         * @private
         */
        updateFromDOM: function () {

            var newSelection = BasePage
                .jQuery('#marketCollectionsMenu .filter-list__item a.-active')
                .data('marketcollectionid');

            selectedMenuId = (typeof newSelection === 'undefined') ? -1 : newSelection;

            return selectedMenuId;
        },
    };
})();

/**
 * Function used to bind event handles to
 * various buttons/links on the page
 *
 * @param   {Object} content jQuery object containing
 *                           the page content
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (content, deps, args) {


    initInternals(content, deps, args);
    content.find('a.tabs__nav-item--justified')
        .off('click.toggleDay')
        .on('click.toggleDay', toggleDay);
    bindRaceToRaceNavigation(content);
    bindCustomNavigation(content);
    nav.init();
    bindNextOffToggle();
};

/**
 * Initialize interface
 *
 * @returns {void}
 *
 * @private
 */
var initInterface = function () {

    /**
     * Helper to init all the click event listeners
     *
     * @returns {void}
     *
     * @private
     */
    var initClickEventListeners = function initClickEventListeners() {
        bindMyWhtvRaces();
        content.find('.race-event__header .filter-list').off('click.racecardCollection')
            .on('click.racecardCollection', 'a', racecardCollectionClickAction);

        content.find('.showAllRaceOverview').off('click.showAllRaceOverview')
            .on('click.showAllRaceOverview', showAllRacesAction);

        content.find('.racecard-runner__name, .racecard-runner__spotlight')
            .off('click.showRunnerOverviewDetails')
            .on('click.showRunnerOverviewDetails', showRunnerOverviewDetails);


        content.find('.racecard-header > a[data-sortid]')
            .off('click.sortRaceCardAction')
            .on('click.sortRaceCardAction', sortRaceCardAction);

        SwiperNavigation.initializeAllSwipers();
    };

    initClickEventListeners();

    // initializes race to race navigation menu above the racecard
    var initRaceToRaceNavigation = function initRaceToRaceNavigation() {
        var raceToRaceList = BasePage.jQuery('.race-to-race-navigation .races-buttons-list');
        var activeRace = raceToRaceList.find('.races-buttons-list__button--active');
        var activeRacePos = +(activeRace && activeRace.offset() && activeRace.offset().left);
        var offsetW = activeRace.width(); // accounts for partially visible active race

        if (activeRacePos > raceToRaceList.width() - offsetW) {

            raceToRaceList.scrollLeft(activeRacePos - offsetW);
        }
    };

    initRaceToRaceNavigation();

    var model = deps.sportsDataRepository.getModel();

    SelectionViewAdapter.init(
        model.selections,
        content
    );

    _.each(
        model.events,
        subscribeToLivescore
    );

    Tabs.init();
};

/**
 * Init model for later usage
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
var initModel = function initModel(regenerateModel) {

    BasePage.setOddsFormat();
    if (regenerateModel && regenerateModel === true) {
        BasePage.ModelComposer.regenerateMergedModel();
    }
    BasePage.PageCommon.PushAdapter.unsubscribeAll();
    _.each(
        deps.sportsDataRepository.getModel(),
        subscribeSetToPush
    );
};

/**
 * Adds to the current model the new modelBox for later usage.
 *
 * @returns {void}
 *
 * @private
 */
var updateModel = function () {

    BasePage.setOddsFormat();
    BasePage.ModelComposer.regenerateMergedModel(window.WH.sportsbook.model);
    BasePage.PageCommon.PushAdapter.unsubscribeAll();
    _.each(
        deps.sportsDataRepository.getModel(),
        subscribeSetToPush
    );
};

/**
 * Fetch open bet results for any settled racecards on the page
 *
 * @returns {void}
 *
 * @private
 */
var obFetchResults = function () {

    /**
     * A function for the loop
     *
     * @params {Object} A DOM element
     *
     * @returns {Void}
     *
     * @private
     */
    var racecard = function (rc) {
        var id = rc.parentNode.getAttribute('data-entityid');
        var competitionId = rc.parentNode.getAttribute('data-competitionid');
        var errorCallback = function (error) {

            rc.querySelector('.processing').classList.add('displayNone');
            rc.querySelector('.error').classList.remove('displayNone');
            console.error(error); // jshint ignore: line
        };

        // generate type code
        var typeCode = RacecardResultsViewProvider.getTypeCode({
            isHorse: args.sport.indexOf('horse') !== -1,
            isMeetingMarket: rc.parentNode.getAttribute('data-isMeetingMarket'),
        });

        // meeting market data
        if (typeCode === 'MM') {
            return ResultsApiAdapter.getMarketsByMeetingEventId(id,
                function (data) {

                    // shift data
                    data = { markets: data };

                    // decorator
                    data.name = rc.parentNode.getAttribute('data-meetingMarketEventName');

                    // render template
                    RacecardResultsViewProvider.render(typeCode, rc, data);

                },
                errorCallback
            );
        }

        // racecard data
        ResultsApiAdapter.getRaceCardByMeetingEventId(id,

            function (data) {
                // decorator
                if (!data.__decorated) {
                    decorateOBResultsRacecard(data);
                    decorateOBResultsRacecardPartTwo(data);
                    decorateOBResultsRacecardPartThree(data);
                }
                data.competitionId = competitionId;
                // render template
                RacecardResultsViewProvider.render(typeCode, rc, data);
            },
            errorCallback
        );

    };

    Array.prototype.slice.call(content[0]
        .getElementsByClassName('racecardResult')).forEach(racecard);
};

/**
 * Decorates racecard data before it's passed over to the template renderer.
 *
 * @params {Object} data from OB
 * @params {Object} dom element
 *
 * @returns {void}
 *
 * @private
 */
var decorateOBResultsRacecard = function (data) {

    resultsRacecardDataformatDistances(data);
    resultsRacecardCalculateHasNonRunners(data);
    // favourites
    data.favourites = {};
    resultsRacecardFindFavRunner(data);
    resultsRacecardFindFavRunner2(data);
};

/**
 * Continues to decorates racecard data before it's passed over to the template renderer.
 *
 * @params {Object} data from OB
 *
 * @returns {void}
 *
 * @private
 */
var decorateOBResultsRacecardPartTwo = function (data) {

    resultsRacecardFilterRunners(data);
    resultsRacecardCalculateHasRunnerSP(data);
    resultsRacecardCalculateHasDividends(data);
    resultsRacecardFormatRule4Dates(data);
    resultsRacecardFormatWinningTime(data);
};

/**
 * Continues to decorates racecard data before it's passed over to the template renderer.
 *
 * @params {Object} data from OB
 *
 * @returns {void}
 *
 * @private
 */
var decorateOBResultsRacecardPartThree = function (data) {

    resultsRacecardFormatOffDate(data);
    resultsRacecardFormatStartDate(data);
    resultsRacecardCalculatePostionExtentions(data);
    resultsRacecardCalculateDeadHeat(data);
    resultsRacecardAddDisplayData(data);
    // mark
    data.__decorated = true;
};

/**
 * Perform initalization of model and interface
 *
 * @params {Boolean} regenerateModel
 * @params {Boolean} isRebindMeetingsMenu
 *
 * @returns {void}
 *
 * @private
 */
var performInit = function (regenerateModel, isRebindMeetingsMenu) {

    Streaming.reset();
    destroyPlayers();

    Streaming.init(bindMyWhtvRaces);
    rebindMeetingsMenu(isRebindMeetingsMenu);
    initModel(regenerateModel);
    initInterface();
    obFetchResults();
    domOperations();
};

/**
 * Perform initialization of model if a new fragment is loaded and updates it if necessary
 * and initializes interface.
 *
 * @returns {void}
 *
 * @private
 */
var performFragmentInit = function performFragmentInit() {

    Streaming.reset();
    destroyPlayers();

    Streaming.init(bindMyWhtvRaces);
    if (Object.keys(couponRaces).length > 1) {

        updateModel();
    }
    else {

        initModel(true);
    }
    initInterface();
    obFetchResults();
    domOperations();
};

/**
 * Performing dom operations
 *
 * @returns {void}
 *
 * @private
 */
var domOperations = function domOperations() {

    LazyLoadingImage.init();
};

/**
 * Function rebinds left menu navigation only if rebind flag is true
 * @param {Boolean} rebind
 *
 * @returns {void}
 *
 * @private
 */
var rebindMeetingsMenu = function (rebind) {

    if (rebind === true) {
        bindCustomNavigation(content);
    }
};

/**
 * Inits operations after render
 *
 * @returns {void}
 *
 * @public
 */
var postRenderInit = function () {
    performInit();
};

/**
 * Update main page title on mobile devices
 * Changing view between meetings and next off requires updates of title which is outside of replaced fragment
 *
 * @param {String} key meetings or nextOff
 *
 * @returns {void}
 */
var updateTitle = function updateTitle(key) {

    var newTitle = $meetingsMainTitle.data('title-' + key);

    if (newTitle) {
        $meetingsMainTitle.text(newTitle);
    }

};

/**
 * Contextual Nav Action
 *
 * @returns {Boolean}
 *
 * @private
 */
var contextualNavAction = function contextualNavAction() {

    var $btn = BasePage.jQuery(this);

    $contextualNav.find('li.contextual-nav__item').removeClass('contextual-nav__item--active');
    $btn.parent('li.contextual-nav__item').addClass('contextual-nav__item--active');
};

/**
 * Contextual Nav Menu Action
 *
 * @returns {Boolean}
 *
 * @private
 */
var contextualNavMenuAction = function contextualNavMenuAction() {

    updateTitle('meetings');

    //Do action only if menu is not active at the moment
    if (!$offCanvas.hasClass(activeMenuClass)) {

        showMenu(true);
        BasePage.PageCommon.navigate(
            URLProvider.meetings.activePanel(args, 'menu'),
            false,
            false
        );
    }
    return false;
};

/**
 * Contextual Nav NextOff Action
 *
 * @returns {Boolean}
 *
 * @private
 */
var contextualNavNextOffAction = function contextualNavNextOffAction() {

    updateTitle('nextoff');

    hideMenu();
    nextOffClickAction();
    return false;
};

/**
 * Function hides left off canvas menu
 *
 * @param {Boolean} hideCarousel Flag to hide or not contextual nav carousel
 *
 * @returns {Boolean}
 *
 * @private
 */
var hideMenu = function (hideCarousel) {

    $offCanvas.removeClass(activeMenuClass);
    window.scrollTo(0, 0);
    if (hideCarousel) {

        $contextualNav.addClass('displayNone');
        $meetingsMainTitle.parents('.header-panel').addClass('displayNone');
    }
    return false;
};

/**
 * Function shows left off canvas menu
 *
 * @param {Boolean} showCarousel Flag to unhide or not contextual nav carousel
 *
 * @returns {Boolean}
 *
 * @private
 */
var showMenu = function (showCarousel) {

    $offCanvas.addClass(activeMenuClass);
    window.scrollTo(0, 0);
    if (showCarousel) {

        $contextualNav.removeClass('displayNone');
        $meetingsMainTitle.parents('.header-panel').removeClass('displayNone');
    }
    return false;
};

/**
 * Function toggles 'Featured' label visibility for highlights in left menu
 *
 * @param   {Object} event   Event model
 *
 * @returns {void}
 *
 * @private
 */
var toggleMenuFeatured = function (show) {

    content.find('#meetings-nav').find('.off-canvas__left-exit').find('.featured')
        .toggleClass('displayNone', !show);
};

/**
 * Check if the menu contains any collection in order to display or hide it
 *
 * @return {void}
 *
 * @private
 */
var updateMenuVisibility = function () {

    BasePage.jQuery('#marketCollectionsMenu')
        .toggleClass('displayNone', !collectionsAvailable());
};

/**
 * Checks for available collections
 *
 * @returns {boolean}
 *
 * @private
 */
var collectionsAvailable = function () {

    return (BasePage.jQuery('#marketCollectionsMenu .filter-list__item a').length > 0);
};

/**
 * Subscribe an in play event to livescore
 * updates
 *
 * @param   {Object} event   Event model
 * @param   {String} eventId Event ID
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToLivescore = function (event, eventId) {

    if (event.betInRun) {
        LivescoreDiffusionAdapter.subscribe(
            BasePage.getSportEnSlug(event),
            eventId,
            EventViewAdapter.livescore(eventId, content).update
        );
    }
};

/**
 * Subscribe a set of entities (events, markets, etc)
 * to PUSH updates
 *
 * @param   {Object} set An object containing entities
 *                       such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function (set) {

    _.each(set, subscribeEntityToPush);
};

/**
 * Subscribe a single entity to PUSH updates
 *
 * @param   {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function (entity) {

    if (entity && entity.id && !entity.id.match(/OB_TY/g) && !entity.settled &&
        BasePage.PageCommon.DomPushAdapter.subscribe(entity, push.callback)) {

        BasePage.PageCommon.PushAdapter.subscribe(entity.pdsTopic);
    }
};

/**
 * Init internal data for later usage
 *
 * @param   {Object} content jQuery container element
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var initInternals = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;
};

/**
 * Function used to load the data for the next off button
 * We bind this function to the next off function
 * This will get the partial from the server and render in the race card area
 *
 * @returns {void}
 *
 * @private
 */
var nextOffClickAction = function () {

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    var afterSuccess = function () {

        performInit(true);

        // Update the filter's status
        content.find('.race-meeting__selection--selected')
            .removeClass('race-meeting__selection--selected');
    };

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.meetings.highlights(args),
        'redirectURL': URLProvider.meetings.activePanel(args, 'next-off'),
    }, '.primary__contenct__wrapper', {
        'afterSuccess': afterSuccess,
    });
};

/**
 * Toggle day / tomorrow & reload partial
 *
 * @returns {void}
 *
 * @private
 */
var toggleDay = function (event) {

    var todayContainer = BasePage.jQuery(content.find('.tabs__navcontainer-today')[0]);
    var tomorrowContainer = BasePage.jQuery(content.find('.tabs__navcontainer-tomorrow')[0]);
    var isToday = event.currentTarget.classList.contains('today');

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */

    var afterSuccess = function (forToday) {

        // Update the today/tmw tab className
        content.find('.tabs__nav-item--active')
            .removeClass('tabs__nav-item--active');
        event.currentTarget.classList.add('tabs__nav-item--active');

        // apply nav cache
        nav.applyCache();

        var toggleMenuDay = function () {
            // show hide container
            if (forToday === 'today') {
                todayContainer.show();
                tomorrowContainer.hide();
            } else {
                todayContainer.hide();
                tomorrowContainer.show();
            }
        };
        toggleMenuDay();
        performInit(true);
        updateMultiselectionMarkup();
    };

    var doLogic = function () {

        var handleSwitchDay = function (day, container) {

            if (container.contents().length > 1) {
                // already fetched, switch
                afterSuccess(day);
            } else {

                // request today, then switch
                deps.pageCommon.reloadSnippet({
                    'requestURL': URLProvider.meetings.navigation(args) +
                    '/' + day,
                }, '.tabs__navcontainer-' + day, {
                    'afterSuccess': function () {
                        bindCustomNavigation(content);
                        afterSuccess(day);
                    },
                });
            }
        };

        // switch back to today
        if (isToday) {

            handleSwitchDay('today', todayContainer);

            // tomorrow already loaded?
        } else {

            handleSwitchDay('tomorrow', tomorrowContainer);
        }
    };

    doLogic();
};

/**
 * This functions resets the internal status of multiselection to
 * compose a coupon.
 *
 * @returns {void}
 *
 * @private
 */
var resetRacecardMultiSelection = function resetRacecardMultiSelection() {

    couponRaces = {};
    content.find('#asideMeetingsNavigation')
        .find('.race-meeting__plus--active.-added')
        .removeClass('race-meeting__plus--active -added');
    setRacesNumberInHeader(0);
};

/**
 * This functions ensures that meeting navigation container that includes the link element is expanded
 *
 * @param {jqElement) highlightedElement - element that gets highlighted in the left nav
     *
 * @returns {void}
 *
 * @private
 */
var ensureParentCompetitionExpanded = function (highlightedElement) {

    var expandSection = function (selector) {

        var container = highlightedElement.parents(selector).first();
        var header = container.find(selector + '-header');

        if (!header.hasClass('-expanded')) {
            header.click();
        }
    };

    expandSection('.race-nav__group');
    expandSection('.race-subnav__group');
};

/**
 * This functions returns the number of coupons that are displayed in
 * the current state of the page.
 *
 * @returns {Number}
 *
 * @private
 */
var visibleCouponRaces = function visibleCouponRaces() {

    return (_.where(couponRaces, { displayed: true }))
        .length;
};

/**
 * Function used to load the data for the allRaces button
 * We bind this function to the allRaces function
 * This will get the partial from the server and render in the race card
 *
 * @returns {void}
 *
 * @private
 */
var allRacesClickAction = function () {

    var allRacesLinkElement = BasePage.jQuery(this);
    resetRacecardMultiSelection();

    args.competitionId = allRacesLinkElement.data('competitionid');

    args.day = allRacesLinkElement.data('day');

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    var afterSuccess = function () {
        performInit(true);

        // set button filter
        var allRacesNavBtn = content.find('.race-meeting__allRaces[data-competitionid=' + args.competitionId + ']');
        nav.set(allRacesNavBtn);

        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);

        ensureParentCompetitionExpanded(allRacesNavBtn);

    };

    var competitionName = BasePage.jQuery('.race-subnav__group#' + args.competitionId +
        ' .race-subnav__group-header-name').text();

    // reload the snippet and set the url and history

    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.meetings.allRaces(args),
        'redirectURL': URLProvider.meetings.allRaces(
            _.extend({}, args, {
                competitionName: competitionName,
            }),
            true),
    }, '.primary__contenct__wrapper', {
        'afterSuccess': afterSuccess,
    });


};

/**
 * Function used to load the data for a racecard
 * based on the clicked meetingNav element
 *
 * @returns {void}
 *
 * @private
 */
var racecardClickAction = function () {
    var clickedElement = BasePage.jQuery(this);
    args.raceId = clickedElement.data('entityid');
    var racecardLinkElement = content.find('.race-meeting__selection[data-entityid=' + args.raceId + ']');

    /**
     * After success markup updates
     *
     * @param {String} elemCls - selector for the element that should get a css update
     *
     * @returns {void}
     *
     * @private
     */
    var markupUpdate = function (elemCls) {

        var elemSelCls = elemCls + '--selected';
        // Update the filter's status
        content.find(elemSelCls).removeClass(elemSelCls);
        racecardLinkElement.addClass(elemSelCls);

        ensureParentCompetitionExpanded(racecardLinkElement);
    };

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    var afterSuccess = function () {

        performInit(true);

        // set nav
        nav.set(racecardLinkElement);

        markupUpdate('.race-meeting__selection');
        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);
    };

    /**
     * Pre-render action
     *
     * @param {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    var preRender = function (data) {

        data = BasePage.jQuery(data);

        if (data.find('.race-event__name h1').text().trim() === '') {
            data.find('.race-event__name h1').text(racecardLinkElement.data('racename'));
        }

        return data;
    };

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.meetings.racecard(args),
        'redirectURL': URLProvider.meetings.racecard(
            _.extend({}, args, {
                raceName: clickedElement.data('racename'),
            }), true),
    }, '.primary__contenct__wrapper', {
        'afterSuccess': afterSuccess,
        'preRender': preRender,
    });
};

/**
 * Click Action for column headings on a racecard, this sets up the parameters needed for sort the racecard
 * function.
 *
 * @returns {boolean} Returns false if it doesn't need to sort.
 *
 * @private
 */
var sortRaceCardAction = function sortRaceCardAction() {

    var headerClicked = BasePage.jQuery(this);

    var params = generateSortParameters(headerClicked, setSortingDataAttribute);

    // Call the function associated with the header...
    params.sortField.sortFunction(params);
};


/**
 * Function to return a set of parameters for the various sorting functions.
 *
 * @param headerClicked {object} jQuery object
 * @param sortAscendingFunction {function} Function to call to get the right sort order.
 * @returns {void}
 */
var generateSortParameters = function generateSortParameters(headerClicked, sortAscendingFunction) {


    var sortColumn = headerClicked.attr('data-sortid');
    var sortField = '';

    var params = {};
    var items;

    // now depending on which class we have we can figure out which data item to assign.
    // ie the element that contains the value we want to sort by.
    sortField = sortObjects[sortColumn];

    // params object, passed down to pretty much every function.
    params = {
        sortField: sortField, // the field that has the value we're looking for
        excludedItems: '.racecard-runner--unnamed-favourite, :has(.racecard-runner__non-runner)',
        // stuff we don't want to sort.
    };

    // Figure out if we're sorting ascending or descending..
    params.sortAscending = sortAscendingFunction(headerClicked);

    //setSortingDataAttribute(headerClicked);

    // Set the parent element for the sorting functions.
    params.parent = headerClicked.parent().next('[id$=__racecard]');

    // grab the entityId

    params.entityId = headerClicked.parent().data('entityid');

    // setup our array of items...

    items = params.parent.children('article[data-entityid=' + params.entityId + ']').not(params.excludedItems);

    // Create array as it's faster than sorting the DOM.
    params.itemsArray = [].slice.call(items);

    return params;

};

/**
 * Function to return the header attribute for a price column, used when we get a push update
 *
 * @param headerClicked {object} jQuery object
 * @returns {boolean}
 */
var getSortingAttributeForPushUpdate = function getSortingAttributeForPushUpdate(headerClicked) {

    return (headerClicked.attr('data-sortasc') === 'true');
};

/**
 * Sets data-sortasc to either true or false on the column heading that's been clicked
 *
 * @param headerClicked JQuery object representing the header that was clicked
 *
 * @returns {boolean} Returns weather the current column is sorting ascending.
 *
 * @private
 */
var setSortingDataAttribute = function setSortingDataAttribute(headerClicked) {

    // first up figure out which column was clicked.
    var currentSortingColumn = headerClicked.siblings('[data-sortasc]');

    /*
         * The reason for the evaluation below is that because we're siblings to find the header
         * with the current racecard it can return a different type of object depending if it found itself or not.
         *
         * When it does find itself you get an object with context that doesn't have the attr method.
         */

    var currentClass = (typeof currentSortingColumn.attr('class') === 'undefined' ) ?
        currentSortingColumn.context.className : currentSortingColumn.attr('class');

    if (currentClass === headerClicked.attr('class')) {

        // So the user has clicked the same column to toggle the sort order to/from ascending/descending...
        cycleHeaderSort(headerClicked);

    } else {

        // We've clicked a new column so remove the attribute from the current one
        currentSortingColumn.removeAttr('data-sortasc');

        // and add it to the new one!
        headerClicked.attr('data-sortasc', 'true');

        // if we're clicking on RP we force descending on the first click so that
        // the highest ranked runners go on top..
        rpHeaderAttributeOverride(headerClicked);
    }
    return (headerClicked.attr('data-sortasc') === 'true');
};

/**
 * Function to force descending sort order on first click of the RP header.
 *
 * @param headerClicked JQuery object representing the header that was clicked.
 *
 * @returns {void}
 *
 * @private
 */
var rpHeaderAttributeOverride = function rpHeaderAttributeOverride(headerClicked) {

    if (headerClicked.hasClass('racecard-header__racing-post')) {

        headerClicked.attr('data-sortasc', 'false');
    }
};

/**
 * Function to cycle the data-sortasc attribute on racecard headers
 *
 * @param headerClicked
 *
 * @returns {void}
 *
 * @private
 */
var cycleHeaderSort = function cycleHeaderSort(headerClicked) {

    // Pretty simple here, if the data attribute is true, make it false etc..
    if (headerClicked.attr('data-sortasc') === 'true') {

        headerClicked.attr('data-sortasc', 'false');
    } else {

        headerClicked.attr('data-sortasc', 'true');
    }

};

/**
 * Sort items by price.
 *
 * @param params - object containing parameters created in sortRaceCardAction
 *
 * @returns {void}
 *
 * @private
 */
function sortItemsByPrice(params) {

    var items;

    // Check if we're ascending or descending.
    if (params.sortAscending === true) {

        items = sortByPriceAscending(params);
    } else {

        items = sortByPriceDescending(params);
    }


    addSortedElementsToDom(params);

}

/**
 * Function to sort by price ascending.
 *
 * @param params - object containing parameters created in sortRaceCardAction
 *
 * @returns {void}
 *
 * @private
 */
function sortByPriceAscending(params) {

    var firstValue = [];
    var secondValue = [];

    var firstId;
    var secondId;

    var orF = function orF(firstValue, secondValue) {
        return (firstValue[1] / firstValue[0] > secondValue[1] / secondValue[0]) ? -1 : 0;
    };


    return params.itemsArray.sort(function (a, b) {

        /* Ok what we're doing here is grabbing the odds for example 14/1,
             * firstValue[0] = 14, firstValue[1] = 1 then doing a simple division
             * to rank the items.
             */
        firstId = BasePage.jQuery(':button', a).attr('data-entityid');
        secondId = BasePage.jQuery(':button', b).attr('data-entityid');

        firstValue[0] = parseInt(window.WH.sportsbook.model.selections[firstId][params.sortField.valueItem[0]]);
        firstValue[1] = parseInt(window.WH.sportsbook.model.selections[firstId][params.sortField.valueItem[1]]);

        secondValue[0] = parseInt(window.WH.sportsbook.model.selections[secondId][params.sortField.valueItem[0]]);
        secondValue[1] = parseInt(window.WH.sportsbook.model.selections[secondId][params.sortField.valueItem[1]]);


        return (firstValue[1] / firstValue[0] < secondValue[1] / secondValue[0]) ?
            1 : orF(firstValue, secondValue);
    });
}

/**
 * Function to sort by price descending.
 *
 *
 * @param params - object containing parameters created in sortRaceCardAction
 *
 * @returns {void}
 *
 * @private
 */
function sortByPriceDescending(params) {

    var firstValue = [];
    var secondValue = [];

    var firstId;
    var secondId;

    var orF = function orF(firstValue, secondValue) {
        return (firstValue[0] / firstValue[1] < secondValue[0] / secondValue[1]) ? 1 : 0;
    };


    return params.itemsArray.sort(function (a, b) {

        /* Ok what we're doing here is grabbing the odds for example 14/1,
             * firstValue[0] = 14, firstValue[1] = 1 then doing a simple division
             * to rank the items.
             */

        firstId = BasePage.jQuery(':button', a).attr('data-entityid');
        secondId = BasePage.jQuery(':button', b).attr('data-entityid');

        firstValue[0] = parseInt(window.WH.sportsbook.model.selections[firstId][params.sortField.valueItem[0]]);
        firstValue[1] = parseInt(window.WH.sportsbook.model.selections[firstId][params.sortField.valueItem[1]]);

        secondValue[0] = parseInt(window.WH.sportsbook.model.selections[secondId][params.sortField.valueItem[0]]);
        secondValue[1] = parseInt(window.WH.sportsbook.model.selections[secondId][params.sortField.valueItem[1]]);

        return (firstValue[0] / firstValue[1] > secondValue[0] / secondValue[1]) ?
            -1 : orF(firstValue, secondValue);
    });
}

/**
 * Sort racecard runners alphabetically, used for the Selection sorting.
 *
 * @param params - object containing parameters created in sortRaceCardAction
 *
 * @returns {void}
 *
 * @private
 */
function sortItemsByAlpha(params) {

    var firstValue;
    var secondValue;

    var firstId;
    var secondId;

    // Check if we're ascending or descending.
    if (params.sortAscending === true) {

        params.itemsArray.sort(function (a, b) {

            firstId = BasePage.jQuery(':button', a).attr('data-entityid');
            secondId = BasePage.jQuery(':button', b).attr('data-entityid');

            firstValue = window.WH.sportsbook.model.selections[firstId][params.sortField.valueItem];
            secondValue = window.WH.sportsbook.model.selections[secondId][params.sortField.valueItem];

            return (secondValue < firstValue) ? 1 : orFAsc(secondValue, firstValue);
        });
    } else {

        params.itemsArray.sort(function (a, b) {

            firstId = BasePage.jQuery(':button', a).attr('data-entityid');
            secondId = BasePage.jQuery(':button', b).attr('data-entityid');

            firstValue = window.WH.sportsbook.model.selections[firstId][params.sortField.valueItem];
            secondValue = window.WH.sportsbook.model.selections[secondId][params.sortField.valueItem];
            return (firstValue > secondValue) ? -1 : orFDescAlpha(firstValue, secondValue);
        });
    }
    addSortedElementsToDom(params);
}

/**
 * Sort race card runners by integer value, used on the Number, Draw and RP headings.
 *
 * @param params - object containing parameters created in sortRaceCardAction
 *
 * @returns {void}
 *
 * @private
 */
function sortItemsByValue(params) {

    var firstValue;
    var secondValue;
    // Check if we're ascending or descending.
    if (params.sortAscending === true) {

        params.itemsArray.sort(function (a, b) {

            // Grab the number from within our element, the reason we're using a regex is that this function sorts
            // multiple columns, the Draw column has (1) rather than just the integer.
            firstValue = getValueBasedOnParams(a, params);
            secondValue = getValueBasedOnParams(b, params);

            // We can end up with NaN here because the RP column sometimes has a blank value.
            firstValue = convertNanToZero(firstValue);
            secondValue = convertNanToZero(secondValue);

            return (firstValue < secondValue) ? -1 : orFDesc(firstValue, secondValue);
        });

    } else { // if(params.sortAscending === true) {

        params.itemsArray.sort(function (a, b) {

            // Grab the number from within our element, the reason we're using a regex is that this function sorts
            // multiple columns, the Draw column has (1) rather than just the integer.
            //firstValue = parseInt(/\d+/.exec(BasePage.jQuery(params.sortField.css, a).html()));
            //secondValue = parseInt(/\d+/.exec(BasePage.jQuery(params.sortField.css, b).html()));
            firstValue = getValueBasedOnParams(a, params);
            secondValue = getValueBasedOnParams(b, params);

            // We can end up with NaN here because the RP column sometimes has a blank value.
            firstValue = convertNanToZero(firstValue);
            secondValue = convertNanToZero(secondValue);

            return (firstValue < secondValue) ? 1 : orFAsc(firstValue, secondValue);
        });
    } // if(params.sortAscending === true) {

    addSortedElementsToDom(params);
}

/**
 * Function to check if we're fetching a value from an HTML element OR from WH.sportsbook.model.selections
 *
 * @param element - HTML element to analyse
 * @param params - params object created in sortRaceCardAction
 *
 * @returns {integer} the value of the specific element.
 *
 * @private
 */
function getValueBasedOnParams(element, params) {

    var id;

    if (params.sortField.htmlValue) {

        // So we're grabbing the value from HTML
        return parseInt(/\d+/.exec(BasePage.jQuery('[data-sortid=' + params.sortField.valueItem + ']', element)
            .html()));

    } else {

        id = BasePage.jQuery(':button', element).attr('data-entityid');
        return parseInt(window.WH.sportsbook.model.selections[id][params.sortField.valueItem]);
    }
}

/**
 * Function to return the OR section of a ternary operator within the sorting functions.
 * Needed for circumventing JSHint cyclomatic errors.
 *
 * @param firstValue {integer}
 * @param secondValue {integer}
 *
 * @returns {integer}
 *
 * @private
 */
var orFDesc = function orFDesc(firstValue, secondValue) {

    return (firstValue > secondValue) ? 1 : 0;
};

/**
 * Needed for circumventing JSHint cyclomatic errors.
 *
 * @param firstValue {integer}
 * @param secondValue {integer}
 *
 * @returns {integer}
 *
 * @private
 */
var orFDescAlpha = function orFDescAlpha(firstValue, secondValue) {

    return (firstValue < secondValue) ? 1 : 0;
};

/**
 * Function to return the OR section of a ternary operator within the sorting functions.
 * Needed for circumventing JSHint cyclomatic errors.
 *
 * @param firstValue
 * @param secondValue
 *
 * @returns {integer}
 *
 * @private
 */
var orFAsc = function orFAsc(secondValue, firstValue) {

    return (secondValue > firstValue ) ? -1 : 0;
};

/**
 * Function to apply the ordered elements to the DOM.
 *
 * @param params - params object created in sortRaceCardAction
 *
 * @returns {void}
 *
 * @private
 */
var addSortedElementsToDom = function addSortedElementsToDom(params) {

    var frag = document.createDocumentFragment();
    var container = params.parent[0];
    for (var i = 0; i < params.itemsArray.length; i++) {
        frag.appendChild(params.itemsArray[i]);
    }

    container.insertBefore(frag, container.firstChild);
};

/**
 * This function is for when RP numbers are empty on the racecard, used by the sortByValue function.
 *
 * @param value {integer} or NaN
 *
 * @returns {integer}
 *
 * @private
 */
var convertNanToZero = function (value) {
    return (isNaN(value) ? 0 : value);
};

/**
 * Returns true if a particular race is displayed in the page in the multirace coupon
 *
 * @params raceId Entity id of a race
 *
 * @returns {Boolean}
 *
 * @private
 */
var raceCouponDisplayed = function raceCouponDisplayed(raceId) {

    if (couponRaces.hasOwnProperty(raceId)) {

        return couponRaces[raceId].displayed;
    }
    return false;
};

/**
 * Prints the number of races in the displays to the user
 *
 * @params num Number of races
 *
 * @returns {void}
 *
 * @private
 */
var setRacesNumberInHeader = function setRacesNumberInHeader(num) {

    var labelProp = (num === 1) ? 'raceAdded' : 'racesAdded';
    var label = Translator.translate('views.templates.racecardsTools.' + labelProp, { num: num });

    content.find('.race-stickyheader').find('.racesNumber').html(label);
    content.find('.header-panel__title--counter').find('em.racesNumber').text(num);

    setRacesVisibility(num);

    BasePage.jQuery('header.race-stickyheader')
        .find('.race-stickyheader__max')
        .toggleClass('race-stickyheader__max--limit', num === maxCouponRaces);
};

/**
 * Set sticky header title to hiden
 *
 * @returns {void}
 *
 * @private
 */
var hideStickyHeader = function hideStickyHeader() {

    BasePage.jQuery('header.race-stickyheader').addClass('wh-hide');

    StickyHeader.off();
};

/**
 * Set sticky header title to visible
 *
 * @returns {void}
 *
 * @private
 */
var showStickyHeader = function showStickyHeader() {

    stickyHeaderInit();

    BasePage.jQuery('header.race-stickyheader').removeClass('wh-hide');
};

/**
 * Fires stickyHeader Scroll behaviour
 *
 * @returns {void}
 *
 * @private
 */
var stickyHeaderInit = function stickyHeaderScrollInit() {

    var elementToStick;
    var headerHeight = 0;

    if (DeviceDetector.getDeviceOs() === 'desktop' &&
        BasePage.jQuery('header.race-stickyheader').hasClass('wh-hide')) {
        elementToStick = BasePage.jQuery('header.race-stickyheader');
        headerHeight = BasePage.jQuery('#header').outerHeight();
        headerHeight += BasePage.jQuery('#localnavigation-affix-top').outerHeight();
        headerHeight += BasePage.jQuery('#subheader-desktop').outerHeight();
        StickyHeader.init(elementToStick, headerHeight);
    }
};

/**
 * Set multiselector title to hiden
 *
 * @returns {void}
 *
 * @private
 */
var hideMultiselectorTitle = function hideMultiselectorTitle() {

    BasePage.jQuery('.header-panel__multiselection-title').addClass('wh-hide');
};

/**
 * Set multiselector title to visible
 *
 * @returns {void}
 *
 * @private
 */
var showMultiselectorTitle = function showMultiselectorTitle() {

    BasePage.jQuery('.header-panel__multiselection-title').removeClass('wh-hide');
};

/**
 * Establishes the visibility of multirace elements based on the number of races
 *
 * @params num Number of races
 *
 * @returns {void}
 *
 * @private
 */
var setRacesVisibility = function setRacesVisibility(num) {

    if (num === 0) {

        hideStickyHeader();
        hideMultiselectorTitle();
    }
    else {

        showStickyHeader();
        showMultiselectorTitle();
    }
};

/**
 * Updates markup based on the current state of the couponRaces object
 *
 * @returns {void}
 *
 * @private
 */
var updateMultiselectionMarkup = function updateMultiselectionMarkup() {

    if (Object.keys(couponRaces).length > 0) {

        content.find('.race-meeting__selection--selected')
            .removeClass('race-meeting__selection--selected');
        _.each(couponRaces, function (elem) {

            content.find('.race-meeting__plus[data-entityid="' + elem.id + '"]')
                .toggleClass('race-meeting__plus--active -added', elem.displayed);
        });

        setRacesNumberInHeader(visibleCouponRaces());
    }
};


/**
 * Function used to compose a coupon with several racecard
 *
 * @returns {void}
 *
 * @private
 */
var racecardMultiClickAction = function racecardMultiClickAction() {

    var racecardElement = BasePage.jQuery(this);
    args.raceId = racecardElement.attr('data-entityid');

    if (!raceCouponDisplayed(args.raceId) && visibleCouponRaces() === maxCouponRaces) {

        return false;
    }

    multiRaceRenderSnippetFragment(racecardElement);
};

/**
 * Function used to play video
 *
 * @returns {void}
 *
 * @private
 */
var playVideo = function playVideo() {
    const bettingTvType = this.getAttribute('data-betting-tv');
    if (bettingTvType === 'whtv') {
        window.WH.messageBus.publish('mediaplayers.whtv.play');
    } else {
        window.WH.messageBus.publish('mediaplayers.whtv.play', { channel: bettingTvType } );
    }
};

/**
 * Destroys all players created so far by this module.
 *
 * @returns {void}
 *
 * @private
 */
const destroyPlayers = () => {

    players.forEach(p => p.destroy());
    players = [];
};

/**
 * Function used to play video on racecard
 *
 * @returns {void}
 *
 * @private
 */
const playVideoonRacecard = function playVideoonRacecard() {
    const bettingTvType = this.getAttribute('data-betting-tv');
    const id = this.getAttribute('data-entityid');
    const streamContainerId = BasePage.jQuery('section[data-entityid='+id+']');
    streamContainerId.find('.cpt-streaming__video').removeClass('displayNone');

    players.push(
        new window.WH.StreamPlayer({
            ...(bettingTvType === 'whtv' ? {} : { eventId: bettingTvType }),
            streamType: 'BROADCAST',
            container: 'HRStreamContainer',
            locale: window.WH.sportsbook.lang.toLowerCase(),
            backendUrl: window.whStatic.config.streaming.streamBaseURL,
            self: 'TRAF_MEET',
        })
    );
};

/**
 * Function used to play video on racecard
 *
 * @returns {void}
 *
 * @private
 */
const playVideoonRacecardNative = function playVideoonRacecard() {
    const bettingTvType = this.getAttribute('data-betting-tv');
    document.querySelector('.cpt-streaming__video-is-native').removeClass('displayNone');
    players.push(
        new window.WH.StreamPlayer({
            ...(bettingTvType === 'whtv' ? {} : { eventId: bettingTvType }),
            streamType: 'BROADCAST',
            container: 'HRStreamContainerNative',
            locale: window.WH.sportsbook.lang.toLowerCase(),
            backendUrl: window.whStatic.config.streaming.streamBaseURL,
            self: 'TRAF_MEET_NATIVE',
        })
    );
};

/**
 * racecardMultiClickAction Helper
 * Loads a snippet or in case it is still in the DOM shows it.
 *
 * @returns {void}
 *
 * @private
 */
function multiRaceRenderSnippetFragment(racecardElement) {

    var appendSnippet = (Object.keys(couponRaces).length !== 0);

    if (couponRaces.hasOwnProperty(args.raceId) && couponRaces[args.raceId].$elem) {

        couponRaces[args.raceId].$elem.toggleClass('wh-hide');
        couponRaces[args.raceId].displayed = !couponRaces[args.raceId].displayed;
        updateMultiselectionMarkup();
    }
    else {

        couponRaces[args.raceId] = {
            $elem: null,
            $plusButton: racecardElement,
            $singleButton: racecardElement.siblings('.race-meeting__selection'),
            id: args.raceId,
            displayed: true,
        };

        // reload the snippet and set the url and history
        deps.pageCommon.reloadSnippet(
            {
                'requestURL': URLProvider.meetings.racecard(args),
            },
            '.primary__contenct__wrapper',
            {
                'afterSuccess': multiRaceAfterSuccess.bind(null, racecardElement),
                'preRender': multiRacePreRender,
                'afterError': multiRaceAfterError,
            },
            undefined,
            appendSnippet);
    }
}


/** * racecardMultiClickAction Helper.
 * What to do after a successful reload of snippet.
 *
 * @returns {void}
 *
 * @private
 */
function multiRaceAfterSuccess (racecardElement) {

    performFragmentInit();

    // set nav
    nav.set(racecardElement);

    updateMultiselectionMarkup();
    window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);
}

/**
 * racecardMultiClickAction Helper.
 * Pre-render action
 *
 * @param {Object} data
 *
 * @returns {void}
 *
 * @private
 */
function multiRacePreRender(data) {

    data = BasePage.jQuery(data);
    data.find('h1.header-panel__title').remove();
    data.find('section.header-panel').remove();
    data.find('.race-to-race-navigation').remove();
    couponRaces[args.raceId].$elem = data;

    return data;
}

/**
 * racecardMultiClickAction Helper.
 * Cleans the couponRaces object if the load of snippet fails.
 *
 * @returns {void}
 *
 * @private
 */
function multiRaceAfterError() {

    delete couponRaces[args.raceId];
}


/**
 * Function used to show/hide all the runner overview details
 *
 * @returns {Boolean}
 *
 * @private
 */
var showAllRacesAction = function () {

    var el = BasePage.jQuery(this);

    if (!el.hasClass('-expanded')) {

        //Open all
        openAllRacesDetails(el);
    } else {

        //Close all
        closeAllRacesDetails(el);
    }

    return false;
};

/**
 * Function shows all the runner overview details
 *
 * @param {Object} el jQuery Object
 *
 * @returns {void}
 *
 * @private
 */
var openAllRacesDetails = function (el) {

    el.parents('.race-event').find('.racecard-runner__extra-details').addClass('-expanded');
    el.addClass('-expanded');
};

/**
 * Function hides all the runner overview details
 *
 * @param {Object} el jQuery Object
 *
 * @returns {void}
 *
 * @private
 */
var closeAllRacesDetails = function (el) {

    el.parents('.race-event').find('.racecard-runner__extra-details').removeClass('-expanded');
    el.removeClass('-expanded');
};

/**
 * Function used to show/hide the runner overview details that was selected
 *
 * @param   {Object} evt action event
 *
 * @returns {Boolean}
 *
 * @private
 */
var showRunnerOverviewDetails = function (evt) {

    var selection = BasePage.jQuery(evt.target).parents('article').first();
    var selectionOverviewDetails = selection.find('.racecard-runner__extra-details');

    selectionOverviewDetails.toggleClass('-expanded');
    selection.toggleClass('-expanded');
    return false;
};
/**
 * Function used to load the data for a racecard
 * based on the clicked collection element
 *
 * @returns {void}
 *
 * @private
 */
var racecardCollectionClickAction = function () {

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    var afterSuccess = function () {
        performInit(true);
    };

    /**
     * Pre-render action
     *
     * @param {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    var preRender = function (data) {

        data = BasePage.jQuery(data);
        // removal is needed because the whole racecard is requested but only inner element updated
        data.find('section.header-panel').remove();
        data.find('.race-to-race-navigation').remove();

        return data.html();
    };

    var collectionId = BasePage.jQuery(this).attr('data-marketcollectionid');
    var raceId = BasePage.jQuery(this).parents('section.race-event').attr('data-entityid');

    var extendedArgs = _.extend({}, args, {
        raceId: raceId,
        collectionId: collectionId,
    });

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.meetings.racecard(extendedArgs),
    }, '.race-event-wrapper .race-event[data-entityid=' + raceId + ']', {
        'afterSuccess': afterSuccess,
        'preRender': preRender,
    });
};

/**
 * Updates page after loading meeting Markets page
 *
 * @returns {void}
 *
 * @private
 */
var meetingMarketsUpdate = function () {

    updateMenuVisibility();
    menuHandler.updateFromDOM();
};

/**
 * Function used to load the data for a meeting market
 * based on the clicked meetingNav element
 *
 * @returns {void}
 *
 * @private
 */
var meetingMarketsClickAction = function () {

    var clickedElement = BasePage.jQuery(this);
    args.meetingMarketsId = clickedElement.data('meetingmarketsid');

    var meetingMarketsLinkElement = content.find(
        '.race-meeting__selection[data-meetingmarketsid=' + args.meetingMarketsId + ']'
    );

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    var afterSuccess = function () {

        performInit(true);
        meetingMarketsUpdate();

        // Update the filter's status
        content.find('.race-meeting__selection').removeClass('race-meeting__selection--selected');
        meetingMarketsLinkElement.addClass('race-meeting__selection--selected');
        bindCustomNavigation(content);
        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);
    };

    /**
     * Pre-render action
     *
     * @param {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    var preRender = function (data) {

        data = BasePage.jQuery(data);

        if (data.find('.sectionheading__column h1').text().trim() === '') {
            data.find('.sectionheading__column h1')
                .text(meetingMarketsLinkElement.data('meetingmarketsname'));
        }

        return data;
    };

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.meetings.meetingMarkets(args),
        'redirectURL': URLProvider.meetings.meetingMarkets(
            _.extend({}, args, {
                meetingMarketsName: clickedElement.data('meetingmarketsname'),
            }), true),
    }, '.primary__contenct__wrapper', {
        'afterSuccess': afterSuccess,
        'preRender': preRender,
    });
};

/**
 * Handles a click event
 *
 * @returns {boolean}
 *
 * @private
 */
var menuClickHandler = function () {

    var element = BasePage.jQuery(this);
    if (menuHandler.getLoadingStatus()) {
        return false;
    }

    updateAndLoad(element);
};

/**
 * Updates and loads an element
 *
 * @param   {Object} element    jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var updateAndLoad = function (element) {

    BasePage.jQuery('#marketCollectionsMenu .filter-list__item a.filter-list__link--active')
        .removeClass('filter-list__link--active');
    element.addClass('filter-list__link--active');
    handleMenuItemClick(element.data('marketcollectionid'));
};

/**
 * Handles the click on a menu item
 *
 * @param   {Integer} selectingId   id of the selection to handle
 *
 * @returns {void}
 *
 * @private
 */
var handleMenuItemClick = function (selectingId) {

    menuHandler.updateSelectedMenuId(selectingId);
    reloadEventByCollectionId(selectingId);
};

/**
 * Clear intervals and subscriptions and resubscribe new stuff
 *
 * @returns {void}
 *
 * @private
 */
var initNewEventData = function () {

    menuHandler.udpateLoadingStatus(false);

    if (window.WH.sportsbook.eventByMarketCollectionException !== true) {
        // Re bind and subscribe.
        performInit(true, true);
        // subscribe hidden entities to push
        BasePage.manageHiddenEntities(deps);
    }
};

/**
 * Generates the ajax request and binds the callbacks
 *
 * @params {Integer} collectionId   id of the collection to
 *                                  request new content for
 *
 * @return {void}
 *
 * @private
 */
var requestNewContent = function (collectionId) {

    /**
     * Get meetingMarketsId if it is not set up
     *
     * @returns {String} meeting markets ID
     *
     * @private
     */
    var getMeetingMarketsId = function () {

        return args.meetingMarketsId ?
            args.meetingMarketsId : 'OB_EV' + args.raceId;
    };

    args.meetingMarketsId = getMeetingMarketsId();
    menuHandler.udpateLoadingStatus(true);

    args.collectionId = (typeof collectionId === 'undefined') ?
        menuHandler.getSelectedMenuId() :
        collectionId;

    BasePage.jQuery('div.event-page-error').addClass('displayNone');

    deps.pageCommon.reloadSnippet(
        {
            'requestURL': URLProvider.meetings
                .getMeetingMarketsByCollection(args),
        },
        '#eventEntity',
        {
            afterSuccess: initNewEventData,
        },
        'div.mask'
    );
};


/**
 * Reloads the events for a collection id
 *
 * @params {Integer} collectionId   id of the collection to
 *                                  request new content for
 *
 * @returns {void}
 *
 * @private
 */
var reloadEventByCollectionId = function (collectionId) {

    requestNewContent(collectionId);
};

/**
 * Binds the events to allow race-to-race navigation from a racecard view
 *
 * @param   {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var bindRaceToRaceNavigation = function (content) {

    bindRaceToAnotherRaceNavigation(content);
    bindRaceToAnotherMeetingNavigation(content);
};

/**
 * Binds the events to allow race-to-race navigation from a racecard view
 *
 * @param   {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var bindRaceToAnotherRaceNavigation = function (content) {

    var evtNamespace = 'click.raceToRace',
        baseSelector = '.race-to-race-navigation .races-buttons-list__';

    content.off(evtNamespace)
        .on(evtNamespace, baseSelector + 'button[data-entityid]', racecardClickAction);
    // .on(evtNamespace, baseSelector + 'allRaces', allRacesClickAction);
};

/**
 * Scroll to top inside dropdown container on hide event
 *
 * @param   {Object} content event
 *
 * @returns {void}
 *
 * @private
 */
var dropdownScrollToTop = function (event) {
    if (BasePage.jQuery(event.target).get(0).checked) {
        BasePage.jQuery(BasePage.jQuery('.race-to-another-meeting-navigation .dropdown__content').first()).scrollTop(0);
    }
};

/**
 * Binds the events to allow race-to-another-meeting navigation from a racecard view
 *
 * @param   {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var bindRaceToAnotherMeetingNavigation = function (content) {

    var evtNamespace = 'click.raceToAnotherMeeting',
        baseSelector = '.race-to-another-meeting-navigation .dropdown__';

    content.off(evtNamespace)
        .on(evtNamespace, baseSelector + 'nextOff', racecardClickAction)
        .on(evtNamespace, baseSelector + 'content .races-buttons-list__button[data-entityid]', racecardClickAction)
        // .on(evtNamespace, baseSelector + 'anotherMeeting', allRacesClickAction)
        .on('change', baseSelector + 'inner #toggler', dropdownScrollToTop);
};

/**
 * Function used for binding custom behavior to buttons or links on the menu
 *
 * @param   {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var bindCustomNavigation = function (content) {

    var bindings = [{
        selector: '.race-meeting__selection[data-entityid]',
        clickGroup: 'racecard',
        bindFunctions: [
            racecardClickAction,
            hideMenu.bind(this, true),
            toggleMenuFeatured.bind(this, false),
        ],
    }, {
        selector: '.play-in-native',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideoonRacecardNative,
        ],
    },  {
        selector: '.play-in-racecard',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideoonRacecard,
        ],
    }, {
        selector: '.play-in-non-racecard',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideo,
        ],
    }, {
        selector: '.hide-on-mobile',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideo,
        ],
    }, {
        selector: '.click-to-watch',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideo,
        ],
    }, {
        selector: '.race-meeting__plus',
        clickGroup: 'meetingCouponSelector',
        bindFunctions: [
            racecardMultiClickAction,
        ],
    }, {
        selector: 'header.race-stickyheader',
        clickGroup: 'stickyHeader',
        bindFunctions: [
            hideMenu.bind(this, true),
            toggleMenuFeatured.bind(this, false),
            hideStickyHeader,
        ],
    }, {
        selector: '#meetings-back-to-menu-link, #meetings-back-to-menu',
        clickGroup: 'stickyHeader',
        bindFunctions: [
            showMenu.bind(this, true),
            toggleMenuFeatured.bind(this, false),
            showStickyHeader,
        ],
    }, {
        selector: '.race-meeting__selection[data-meetingMarketsId]',
        clickGroup: 'meetingMarkets',
        bindFunctions: [
            meetingMarketsClickAction,
            hideMenu.bind(this, true),
            toggleMenuFeatured.bind(this, false),
            resetRacecardMultiSelection,
        ],
    }, {
        selector: '.race-meeting__allRaces',
        clickGroup: 'allRaces',
        bindFunctions: [
            allRacesClickAction,
            hideMenu.bind(this, true),
            toggleMenuFeatured.bind(this, false),
        ],
    }, {
        selector: '.race-nav__group-header--link',
        clickGroup: 'nextOff',
        bindFunctions: [
            nextOffClickAction,
            hideMenu,
            toggleMenuFeatured.bind(this, true),
        ],
    }, { //the selector below is wrong and will not work with current markup and does not belong to menu
        selector: '#marketCollectionsMenu .filter-list__item a:not(.-active)',
        clickGroup: 'menuClick',
        bindFunctions: [menuClickHandler],
    }, {
        selector: '#carousel li.contextual-nav__item > a',
        clickGroup: 'contextualNav',
        bindFunctions: [
            contextualNavAction,
            resetRacecardMultiSelection,
        ],
    }, {
        selector: '#carousel li.contextual-nav__item > a.meetings-menu-btn',
        clickGroup: 'contextualNavMenu',
        bindFunctions: [
            contextualNavMenuAction,
        ],
    }, {
        selector: '#carousel li.contextual-nav__item > a.meetings-next-off-btn',
        clickGroup: 'contextualNavNextOff',
        bindFunctions: [
            contextualNavNextOffAction,
        ],
    }];

    _.each(bindings, applyBindings.bind(this, content));
};

/**
 * Binds My WHTV Races section in menu
 *
 * @returns {void}
 *
 * @private
 */
var bindMyWhtvRaces = function () {

    var bindings = [{
        selector: '.myWhtvRaces .race-meeting__selection[data-entityid]',
        clickGroup: 'racecard',
        bindFunctions: [
            racecardClickAction,
            hideMenu,
            toggleMenuFeatured.bind(this, false),
        ],
    }, {
        selector: '.play-in-native',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideoonRacecardNative,
        ],
    },  {
        selector: '.play-in-racecard',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideoonRacecard,
        ],
    }, {
        selector: '.play-in-non-racecard',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideo,
        ],
    }, {
        selector: '.hide-on-mobile',
        clickGroup: 'racecard',
        bindFunctions: [
            playVideo,
        ],
    }];

    _.each(bindings, applyBindings.bind(this, content));
};

/**
 * Binds functions to the given binding Object
 *
 * @param {Object} content jQuery element
 * @param {Object} binding Object with required attributes:
 *                              {String} selector jQuery selector
 *                              {String} clickGroup Event Namespace
 *                              {Array} bindFunctions An Array of functions to bind to a click event
 *
 * @returns {void}
 *
 * @private
 */
var applyBindings = function (content, binding) {

    var selector = binding.selector;
    var clickGroup = binding.clickGroup;

    /**
     * Binds a function to the given selector on a click event
     *
     * @param {Object} content jQuery element
     *
     * @returns {void}
     *
     * @private
     */
    var applyBindFunction = function (bindFunction) {

        content.find(selector).on('click.' + clickGroup, bindFunction);
    };

    if (content instanceof BasePage.jQuery) {
        content.find(selector).off('click.' + clickGroup);
        _.each(binding.bindFunctions, applyBindFunction);
    }

};

/**
 * Function to generate a click active price columns when we get a push message
 * to update the current price.
 *
 * @param {}
 *
 * @return {void}
 *
 * @private
 *
 */
var triggerPriceSort = _.debounce(function () {

    // we could have multiple racecards on the screen.
    BasePage.jQuery('[data-sortid=price]').filter('[data-sortasc]').each(function () {

        var headerClicked = BasePage.jQuery(this);

        var params = generateSortParameters(headerClicked, getSortingAttributeForPushUpdate);

        params.sortField.sortFunction(params);
    });


}, 500);


/**
 * Function to generate a click active price columns when we get a push message
 * to update the current price.
 *
 * @param {}
 *
 * @return {void}
 *
 * @private
 *
 */
var updatePreviousOdds = _.debounce(function updatePreviousOddsClousure(message) {

    BasePage.jQuery('#previous_odd_item_' + message.id).html(message.previousPrice);

}, 100);

/**
 * Push related functionality
 *
 * @type {Object}
 */
var push = {

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: function (message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {
        'betInRun': 'betInRunUpdateCase',
        'status': 'statusUpdateCase',
        'currentprice': 'currentpriceUpdateCase',
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * The name of this function is linked to push.handlers
     *
     * @param   {Object}    message     PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        var event = deps.sportsDataRepository.getModel().events[message.id];
        if (message.nodeType === 'events' &&
            event && event.sort && event.sort !== 'TNMT') {

            push.betInRunUpdateEvent(message);
        }
    },

    /**
     * Handles the dom push message containing an status attribute
     * The name of this function is linked to push.handlers
     *
     * @param   {Object}    message     PUSH message
     *
     * @return {void}
     *
     * @private
     */
    statusUpdateCase: function (message) {

        if (message.id.match(/OB_OU/g)) {

            push.statusUpdateSelection(message);
        }
    },

    /**
     * Handles the dom push message containing an price attribute
     * The name of this function is linked to push.handlers
     *
     * @param   {Object}    message     PUSH message
     *
     * @return {void}
     *
     * @private
     */
    currentpriceUpdateCase: function currentpriceUpdateCase(message) {

        if (message.id.match(/OB_OU/g) && message.hasChanged) {

            if (args.sport !== 'greyhounds') {
                triggerPriceSort();
            }

            updatePreviousOdds(message);
        }
    },


    /**
     * Handles the dom push message containing an status attribute
     * For a selection message
     *
     * @param   {Object}    message     PUSH message
     *
     * @return {void}
     *
     * @private
     */
    statusUpdateSelection: function (message) {

        var selection = deps.sportsDataRepository.getModel().selections[message.id];

        if (selection && selection.statusOutModel) {

            delete selection.statusOutModel;
        }
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * for an Event Node propagating changes to the selections
     *
     * @param   {Object}    message     PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateResetSelections: function (message) {

        //LOOP SELECTION WITH FAKE STATUS
        //REMOVE FAKE STATUS
        //UPDATE SELECTIONS WITH ORIGINAL STATUS
        /**
         * Get selections with fake status
         *
         * @param   {Object}    selection   PDS Selection Object
         *
         * @returns {boolean}
         *
         * @private
         */
        var getSelectionsFakeStatus = function (selection) {

            return selection.eventId === message.id &&
                selection.hasOwnProperty('statusOutModel');
        };

        /**
         * Apply changes to selections with fake status
         *
         * @param   {Object}    selection   PDS Selection Object
         *
         * @returns {void}
         *
         * @private
         */
        var updateFakeSelections = function (selection) {

            var disabled = selection.status === 'A' ? false : true;

            push.updateSelectionStatus(selection.id, disabled, selection.status);
            push.updateSelectionStatus(selection.id + '_SP', disabled, selection.status);
            delete selection.statusOutModel;
        };

        var selections = deps.sportsDataRepository.getModel().selections;

        selections = _.filter(selections, getSelectionsFakeStatus);

        _.each(selections, updateFakeSelections);
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * for an Event Node propagating changes to the selections
     *
     * @param   {Object}    message     PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateSuspendSelections: function (message) {

        //CREATE FAKE STATUS
        //SUSPEND THE SELECTIONS
        /**
         * Get event selections
         *
         * @param   {Object}  selection   selection   PDS Object
         *
         * @returns {boolean}
         *
         * @private
         */
        var getEventSelections = function (selection) {

            return selection.eventId === message.id && selection.status === 'A';
        };

        /**
         * Apply changes to the selections
         *
         * @param   {Object}    selection   selection PDS Object
         *
         * @returns {void}
         *
         * @private
         */
        var updateSelection = function (selection) {

            selection.statusOutModel = 'S';
            push.updateSelectionStatus(selection.id, true, 'S');
            push.updateSelectionStatus(selection.id + '_SP', true, 'S');
        };

        var selections = _.filter(deps.sportsDataRepository.getModel().selections, getEventSelections);

        _.each(selections, updateSelection);

        if (BasePage.jQuery('#' + message.id).parent().hasClass('race-event--highlights')) {

            push.updateNextOff(message.id);
        }
    },

    /**
     * Update selection status in the DOM
     *
     * @param   {String}    id          selection id
     * @param   {boolean}   disabled    DOM class action
     * @param   {String}    status      data-status attribute value
     *
     * returns {void}
     *
     * @private
     */
    updateSelectionStatus: function (id, disabled, status) {

        BasePage.jQuery('#' + id)
            .toggleClass('disabled', disabled)
            .attr('data-status', status);
    },

    /**
     * Update next off status for an element
     *
     * @param {String} eventId Event ID
     *
     * @returns {void}
     *
     * @private
     */
    updateNextOff: function updateNextOff(eventId) {

        var nextOffWrapper = BasePage.jQuery('#' + eventId).parent();

        nextOffWrapper.addClass('race-event--highlights--inplay');
        window.setTimeout(
            function () {

                push.removeNextOff(nextOffWrapper);
            },
            5000
        );
    },

    /**
     * Removes next off element that started.
     *
     * @param {Object} element jQuery Element
     *
     * @returns {void}
     *
     * @private
     */
    removeNextOff: function removeNextOff(element) {

        var domEl = element && element[0];

        if (BasePage.jQuery.contains(document, domEl)) {

            var elementToRemove = BasePage.jQuery(element);

            elementToRemove.remove();

            push.checkEmptyNextOff();
        }
    },

    /**
     * Check the Next Off container to see if it's empty or not
     *
     * @returns {void}
     *
     * @private
     */
    checkEmptyNextOff: function checkEmptyNextOff() {

        var nextOffWrapper = BasePage.jQuery('.next-off-slider');

        // Checking against 1, as there will always be 1 with "Please refresh" message in it, hidden
        if (nextOffWrapper.find('.race-event--highlights').length <= 1) {

            nextOffWrapper.find('.race-event--highlights').removeClass('displayNone');
        }
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * for an Event Node
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateEvent: function (message) {

        if (message.value) {

            push.betInRunUpdateSuspendSelections(message);

        } else {

            push.betInRunUpdateResetSelections(message);
        }
    },
};

const TIMEZONE = 'Europe/London';

const isWithinSupportedDays = startDataTime => {
    const today = moment.tz(TIMEZONE).startOf('day');
    const startDateTime = moment.tz(startDataTime, TIMEZONE);

    const dayAfterTomorrow = today.add(2, 'days').endOf('day');

    if (startDateTime.isSameOrBefore(dayAfterTomorrow)) {
        return true;
    }
};

const redirectToNewRacecard = (content, args) => {
    if (args.meetingMarketsId) return;

    if (args.eventId) { // racecard or results
        const activeEvent = content.find('.races-buttons-list__button--active');
        if (!activeEvent.length) return;

        if (isWithinSupportedDays(activeEvent.attr('data-startdatetime'))) {
            window.WH.messageBus.publish(MessageBusTopics.INTERNAL_ROUTE, {
                url: URLProvider.racing.racecard({ ...args, raceName: activeEvent.attr('data-racename') }),
                replace: true,
            });
        }
    }
    else if (args.competitionId && args.day) { // all-races or all-races results
        window.WH.messageBus.publish(MessageBusTopics.INTERNAL_ROUTE, {
            url: URLProvider.racing.allRaces(args),
            replace: true,
        });
    }
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
const api = {

    /**
     * Entry point.
     *
     * @param {Object} PreferencesProvider
     *
     * @returns {void}
     *
     * @public
     */
    init: function (PreferencesProvider) {

        prefs = PreferencesProvider;
    },

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        /**
         * Set of URL testers
         *
         * @type {Object}
         */
        var urlTesters = {
                raceId: function raceId() {

                    return args.raceId ? '/betting/' + args.locale + '/' +
                        args.sport + '/OB_EV' + args.raceId : base;
                },
                meetingMarkets: function meetingMarkets() {

                    return args.meetingMarketsId ? '/betting/' + args.locale + '/' + args.sport +
                        '/meetings/markets/' + args.meetingMarketsId : base;
                },
                allRaces: function allRaces() {

                    return args.competitionId ? '/betting/' + args.locale + '/' + args.sport +
                        '/meetings/' + args.day + '/' + args.competitionId : base;
                },
                activePanel: function activePanel() {

                    return args.activePanel ? '/betting/' + args.locale + '/' + args.sport +
                        '/meetings/' + args.activePanel : base;
                },
                newMeetings: function newMeetings() {
                    if (args.regionFilter) {
                        var composedURL = '/betting/' + args.locale + '/' + args.sport +
                            '/meetings/' + args.regionFilter;
                        if (args.dayFilter) {
                            composedURL += '/' + args.dayFilter;
                        }

                        return composedURL;
                    }

                    return base;
                },
            },
            base = '/betting/' + args.locale + '/' + args.sport + '/meetings';

        base = urlTesters.raceId();
        base = urlTesters.meetingMarkets();
        base = urlTesters.allRaces();
        base = urlTesters.activePanel();
        base = urlTesters.newMeetings();

        return base;
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'meetings', args.sport);

        redirectToNewRacecard(content, args);

        if (deps.ready) {
            deps.ready('meetings');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function () {

        BasePage.switchTab('Meetings');
        BasePage.setOddsFormat();
        postRenderInit();
        meetingMarketsUpdate();
        FirstTimeUserJourney.init(FirstTimeUserJourney.getPages().MEETINGS, args.sport);
        messageBusSubscriptions.push(
            window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_BET_PLACED, function () {

                window.setTimeout(function () {
                    Streaming.init();
                }, streamingsReInitDelay);

            })
        );

        messageBusSubscriptions.push(
            window.WH.messageBus.subscribe(MessageBusTopics.LOGIN_JS_LOGIN_DETERMINED, function () {
                window.setTimeout(function () {
                    Streaming.init();
                }, streamingsReInitDelay);
            })
        );

    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        if (DeviceDetector.isNative()) {
            BasePage.jQuery('.play-in-racecard').addClass('displayNone');
            BasePage.jQuery('.click-to-watch').removeClass( 'hide-on-mobile' );
            BasePage.jQuery('.click-to-watch').addClass('displayNone');
            BasePage.jQuery('.click-to-watch-native').removeClass('displayNone');
            BasePage.jQuery('.watch-text').removeClass( 'show-on-mobile-upper' );
            BasePage.jQuery('.play-in-racecard').addClass( 'displayNone' );
            BasePage.jQuery('.play-in-natives').removeClass( 'displayNone' );
            BasePage.jQuery('.play-in-non-racecard').addClass( 'displayNone' );
            BasePage.jQuery('.betting-text-icon').addClass( 'displayNone' );

            if (!BasePage.PageCommon.isNativeSupportForNewBettingTv()) {
                BasePage.jQuery('.new-native').addClass('displayNone');
                BasePage.jQuery('.legacy-native').removeClass('displayNone');
            }
        }

        rendered = true;
        activeMenuClass = 'off-canvas--move-right';
        $contextualNav = BasePage.jQuery('#carousel');
        $meetingsMainTitle = BasePage.jQuery('#meetingsMainTitle');
        $offCanvas = BasePage.jQuery('#meetings_canvas');
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {

        unsubscribeMessageBus();
        prefs = null;
        content = null;
        deps = null;
        args = null;
        $offCanvas = null;
        $contextualNav = null;
        activeMenuClass = '';
        messageBusSubscriptions = [];
        rendered = false;

        Streaming.reset();
        destroyPlayers();
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        if (DeviceDetector.isNative()) {
            BasePage.jQuery('.play-in-racecard').addClass('displayNone');
            BasePage.jQuery('.click-to-watch').removeClass( 'hide-on-mobile' );
            BasePage.jQuery('.click-to-watch').addClass('displayNone');
            BasePage.jQuery('.click-to-watch-native').removeClass('displayNone');
            BasePage.jQuery('.watch-text').removeClass( 'show-on-mobile-upper' );
            BasePage.jQuery('.play-in-racecard').addClass( 'displayNone' );
            BasePage.jQuery('.play-in-natives').removeClass( 'displayNone' );
            BasePage.jQuery('.play-in-non-racecard').addClass( 'displayNone' );
            BasePage.jQuery('.betting-text-icon').addClass( 'displayNone' );

            if (!BasePage.PageCommon.isNativeSupportForNewBettingTv()) {
                BasePage.jQuery('.new-native').addClass('displayNone');
                BasePage.jQuery('.legacy-native').removeClass('displayNone');
            }
        }

        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'meetings',

};

export default api;
